import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";

const InvoiceForUsha = ({
  invoiceArray,
  firmArray,
  items,
  taxBreakup,
  taxSummary,
}) => {
  // Get data from sessionStorage if props are not provided
  const [sessionData, setSessionData] = useState(null);

  useEffect(() => {
    // Check if we need to get data from sessionStorage
    if (!invoiceArray?.length && !sessionData) {
      try {
        const storedData = sessionStorage.getItem("invoiceData");
        const storedHSNSummaryData = sessionStorage.getItem("HSNSummaryData");
        const storedEInvoiceData = sessionStorage.getItem("EInvoiceData");
        if (storedData) {
          const parsedData = JSON.parse(storedData);
          const parsedHSNSummaryData = JSON.parse(storedHSNSummaryData);
          const parsedEInvoiceData = JSON.parse(storedEInvoiceData);
          setSessionData({
            ...parsedData,
            HSNSummaryData: parsedHSNSummaryData?.HSNSummaryData,
            EInvoiceData: parsedEInvoiceData?.EInvoiceData,
          });
          // Clear the session storage to avoid data persisting
          sessionStorage.removeItem("invoiceData");
        }
      } catch (error) {
        console.error("Error retrieving data from sessionStorage:", error);
      }
    }
  }, [invoiceArray, sessionData]);

  // Use props or session data
  const effectiveInvoiceArray = invoiceArray?.length
    ? invoiceArray
    : sessionData?.invoiceArray || [];
  const effectiveFirmArray = firmArray?.length
    ? firmArray
    : sessionData?.firmArray || [];
  const effectiveItems = items || sessionData?.items || [];
  const effectiveTaxBreakup = taxBreakup || sessionData?.taxBreakup || [];
  const effectiveTaxSummary = taxSummary || sessionData?.taxSummary || [];
  const effectiveEInvoiceData = sessionData?.EInvoiceData || [];
  const effectiveHSNSummaryData = sessionData?.HSNSummaryData || [];
  console.log(effectiveTaxBreakup, effectiveTaxSummary);

  // Use the first elements from the arrays if they exist
  const invoiceData = effectiveInvoiceArray?.[0] || {};
  const firmData = effectiveFirmArray?.[0] || {};

  // Check if we need pagination (more than 20 items)
  const needsPagination = effectiveItems.length > 20;

  // Data object derived from props or session data
  const data = {
    firm: {
      GST: firmData?.GST || "",
      FirmName: firmData?.FirmName || "",
      Address1: firmData?.Address1 || "",
      Address2: firmData?.Address2 || "",
      MobileNo: firmData?.MobileNo || "",
      BankName: firmData?.BankName || "",
      AccountNo: firmData?.AccountNo || "",
      IFSC: firmData?.IFSC || "",
      MsmeNo: firmData?.MsmeNo || "",
      SL1: firmData?.SL1 || "",
      SL2: firmData?.SL2 || "",
    },
    buyer: {
      name: invoiceData?.PartyName || "",
      address: invoiceData?.Address1 || "",
      address2: invoiceData?.Address2 || "",
      gst: invoiceData?.GSTPO ? invoiceData.GSTPO.replace("GST No- ", "") : "",
      invoiceNo: invoiceData?.VoucherNo || "",
      invoiceDate: invoiceData?.Voucher_Date || "",
      challanNo: invoiceData?.ChallanNo || "SEZ",
      challanDate: invoiceData?.Challan_Date || "",
      vehicle: invoiceData?.VehicleNo || "",
      poNo: invoiceData?.PONo || "",
      transport: invoiceData?.Transport || "",
      placeOfSupply: invoiceData?.PlaceOfSupply || "",
    },
    // Use effective items from props or session storage
    items: effectiveItems,
    // Use passed totals if available, otherwise generate default
    totals: {
      taxable: invoiceData?.GrossAmount || 0,
      tax: invoiceData?.TaxationAmount || 0,
      cgst: invoiceData?.TaxationAmount ? invoiceData.TaxationAmount / 2 : 0,
      sgst: invoiceData?.TaxationAmount ? invoiceData.TaxationAmount / 2 : 0,
      roundOff: invoiceData?.RoundedValue || 0,
      grandTotal: invoiceData?.NetAmount || 0,
    },
    // Use effective tax data from props or session storage
    taxBreakup: effectiveTaxBreakup,
    taxSummary: effectiveTaxSummary,
    HSNSummaryData: effectiveHSNSummaryData,
    EInvoiceData: effectiveEInvoiceData,
  };

  const tableHeaderStyle = {
    fontWeight: 700,
    fontSize: 13,
    padding: "8px 5px",
    border: "1px solid #222",
    color: "#000",
    textAlign: "center",
  };

  const tableCellRight = {
    textAlign: "right",
    color: "#000",
    padding: "6px 5px",
    border: "1px solid #ddd",
  };
  const tableCellCenter = {
    textAlign: "center",
    color: "#000",
    padding: "6px 5px",
    border: "1px solid #ddd",
  };
  const tableCellLeft = {
    textAlign: "left",
    color: "#000",
    padding: "6px 5px",
    border: "1px solid #ddd",
  };

  // Print-specific styles
  const printStyles = `
    @media print {
      /* Hide elements not meant for printing */
      .no-print {
        display: none !important;
      }
      
      /* Reset all margins and paddings */
      @page {
        size: A4;
        margin: 0 5mm !important;
      }
      
      html, body {
        margin: 0 !important;
        padding: 0 !important;
        width: 100% !important;
        height: auto !important;
        color: black !important;
        -webkit-print-color-adjust: exact !important;
      }
      
      .page-content {
        padding: 0 !important;
        margin: 0 !important;
        background: white !important;
        color: black !important;
      }
      
      .print-container {
        border: none !important;
        box-shadow: none !important;
        padding: 7mm 10mm !important; /* Reduced from 10mm top padding to 7mm */
        margin: 0 !important;
        width: 100% !important;
        max-width: 100% !important;
        border-radius: 0 !important;
        background: white !important;
        color: black !important;
        transform: none !important;
        transform-origin: top left !important;
        box-sizing: border-box !important;
      }
      
      /* Handle pagination for the item table and post-items section */
      
      /* Make table headers repeat on each page */
      thead { 
        display: table-header-group !important;
      }
      
      /* Ensure the item rows can break across pages naturally */
      tr.item-row {
        page-break-inside: auto !important;
        line-height: 1.1 !important; /* Added line-height control */
      }
      
      /* For proper pagination of items */
      table { 
        page-break-inside: auto !important; 
        margin-top: 0 !important;  /* Remove top margin from tables */
        margin-bottom: 0 !important; /* Remove bottom margin from tables */
      }
      
      tr { 
        page-break-inside: avoid !important; 
      }
      
      /* Critical: Keep the post-items section together */
      .after-items-section {
        page-break-inside: avoid !important;
        padding-top: 5px !important; /* Reduced from 10px to 5px */
        margin-top: 8px !important; /* Reduced from 12px to 8px */
      }
      
      /* For proper spacing on the last page */
      .item-table-container {
        margin-bottom: ${
          needsPagination ? "0" : "3mm"
        } !important; /* Reduced from 5mm to 3mm */
        margin-top: 0 !important; /* Remove top margin */
      }
      
             /* Make table more compact when printing */
       table, tr, td, th {
         font-size: 11px !important;
         padding: 1px 3px !important; /* Reduced from 2px 3px to 1px 3px */
         line-height: 1.2 !important; /* Added line height control */
         border: 1px solid #ddd !important; /* Ensure consistent border in print */
       }
       
       /* Ensure table headers have proper borders in print */
       th {
         border: 1px solid #222 !important;
         background: transparent !important;
         background-color: transparent !important;
       }
       
       /* Ensure main table border is consistent in print */
       .item-table-container table {
         border: 1px solid #222 !important;
       }
      
      /* Adjust font sizes for printing */
      h1, h2, h3, h4, h5, h6 { margin: 2px 0 !important; }
      p, div { margin: 0 !important; line-height: 1.2 !important; }
      
      /* Make the header more compact */
      .invoice-header { margin-bottom: 3px !important; } /* Reduced from 5px to 3px */
      
      /* Adjust spacing between sections */
      .section-spacing { margin-top: 3px !important; margin-bottom: 3px !important; } /* Reduced from 5px to 3px */
      
      /* Preserve spacing for amount in words section */
      .amount-words-section { margin-top: 12px !important; margin-bottom: 5px !important; } /* Reduced from 25px to 12px */
      
      /* Horizontal lines */
      hr {
        margin: 5px 0 !important; /* Reduced margin */
      }
      
      /* Remove all background colors for clean print */
      * {
        background: transparent !important;
        background-color: transparent !important;
        -webkit-print-color-adjust: exact !important;
        print-color-adjust: exact !important;
        color-adjust: exact !important;
        color: black !important;
      }
      
      /* Remove table header background */
      th {
        background: transparent !important;
        background-color: transparent !important;
      }
      
      /* Remove alternating row backgrounds */
      tr:nth-child(even), tr:nth-child(odd) {
        background: transparent !important;
        background-color: transparent !important;
      }
      
      /* Keep terms and conditions together */
      .terms-section {
        page-break-inside: avoid !important;
        margin-bottom: 5px !important; /* Added smaller margin */
      }
      
      .signature-section {
        page-break-inside: avoid !important;
        margin-top: 5px !important; /* Reduced from 10px to 5px */
      }
      
      /* Ensure signature space is maintained when printing, but smaller */
      .signature-name-row {
        margin-bottom: 0 !important;
        padding-bottom: 0 !important;
      }
      
      .signature-space {
        height: 15px !important; /* Reduced from 25px to 15px */
        min-height: 15px !important;
        display: block !important;
        visibility: visible !important;
      }
      
      .signature-title-row {
        margin-top: 0 !important;
        padding-top: 0 !important;
      }
      
      /* All table cells more compact */
      td, th {
        line-height: 1.2 !important;
      }
      
      /* More compact list */
      ol, ul {
        padding-left: 15px !important; /* Reduced from 20px */
        margin: 0 !important;
      }
      
      li {
        margin-bottom: 0 !important;
        line-height: 1.2 !important;
      }
    }
  `;

  // Function to handle printing
  const handlePrint = () => {
    window.print();
  };

  // Added state for copy type
  const [copyType, setCopyType] = useState("ORIGINAL");

  // Helper function to safely format numbers
  const formatNumber = value => {
    if (value === null || value === undefined) return "";
    const numValue = parseFloat(value);
    return isNaN(numValue) ? "" : numValue.toFixed(2);
  };

  // Create a table header component that can be reused for each page
  const ItemTableHeader = () => (
    <thead>
      <tr>
        <th
          style={{
            ...tableHeaderStyle,
            fontSize: 12,
            padding: "4px 3px",
            textAlign: "center",
            width: "6%",
          }}
        >
          SN
        </th>
        <th
          style={{
            ...tableHeaderStyle,
            fontSize: 12,
            padding: "4px 3px",
            textAlign: "left",
            width: "12%",
          }}
        >
          Code
        </th>
        <th
          style={{
            ...tableHeaderStyle,
            fontSize: 12,
            padding: "4px 3px",
            textAlign: "left",
            width: "25%",
          }}
        >
          Description
        </th>
        <th
          style={{
            ...tableHeaderStyle,
            fontSize: 12,
            padding: "4px 3px",
            textAlign: "left",
            width: "10%",
          }}
        >
          Ply
        </th>
        <th
          style={{
            ...tableHeaderStyle,
            fontSize: 12,
            padding: "4px 3px",
            textAlign: "left",
            width: "10%",
          }}
        >
          Calc
        </th>
        <th
          style={{
            ...tableHeaderStyle,
            fontSize: 12,
            padding: "4px 3px",
            textAlign: "left",
            width: "12%",
          }}
        >
          HSN
        </th>
        <th
          style={{
            ...tableHeaderStyle,
            fontSize: 12,
            padding: "4px 3px",
            textAlign: "right",
            width: "10%",
          }}
        >
          Qty
        </th>
        <th
          style={{
            ...tableHeaderStyle,
            fontSize: 12,
            padding: "4px 3px",
            textAlign: "right",
            width: "10%",
          }}
        >
          Rate
        </th>
        <th
          style={{
            ...tableHeaderStyle,
            fontSize: 12,
            padding: "4px 3px",
            textAlign: "right",
            width: "5%",
          }}
        >
          Amount
        </th>
      </tr>
    </thead>
  );

  // Adjust signature space
  const signature = (
    <div
      className="signature-section"
      style={{ width: "30%", fontSize: 12, color: "#000" }}
    >
      <div style={{ textAlign: "right", fontWeight: 600 }}>
        <div className="signature-name-row">
          For <b>{data.firm.FirmName}</b>
        </div>
        {/* This is a spacer specifically for signatures */}
        <div
          className="signature-space"
          style={{ height: 20, border: "0px dashed transparent" }}
        ></div>
        <div className="signature-title-row">Authorized Signatory</div>
      </div>
    </div>
  );

  return (
    <div className="page-content">
      {/* Add print-specific styles */}
      <style>{printStyles}</style>

      <div
        className="print-container"
        style={{
          fontFamily: "Segoe UI, Arial, sans-serif",
          fontSize: 13,
          maxWidth: 900,
          margin: "0 auto",
          background: "#fff",
          border: "1.5px solid #222",
          borderRadius: 8,
          boxShadow: "0 3px 15px rgba(0,0,0,0.15)",
          padding:
            "20px 35px" /* Reduced from 25px to 20px top/bottom padding */,
          color: "#000",
          lineHeight: 1.2,
        }}
      >
        {/* Top spacing with Company Header, TAX INVOICE Title, and QR Code */}
        <div style={{ height: "3.5cm", minHeight: "3.5cm", position: "relative" }}>
          {/* Left Section - Company Header Details */}
          <div style={{ 
            position: "absolute", 
            left: 0, 
            top: 0, 
            width: "60%", 
            fontSize: 11,
            color: "#000"
          }}>
            {/* UDYAM Number */}
            <div style={{ fontWeight: 600, fontSize: 10, marginBottom: 2, color: "#000" }}>
              UDYAM-RJ-22-0037163
            </div>
            
            {/* GSTIN */}
            <div style={{ fontWeight: 600, fontSize: 10, marginBottom: 2, color: "#000" }}>
              GSTIN: {data.firm.GST || ""}
            </div>
            
            {/* Company Name */}
            <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 3, color: "#000" }}>
              {data.firm.FirmName || ""}
            </div>
            
            {/* Business Lines */}
            <div style={{ fontSize: 10, marginBottom: 1, color: "#000" }}>
              {data.firm.SL1 || ""}
            </div>
            <div style={{ fontSize: 10, marginBottom: 3, color: "#000" }}>
              {data.firm.SL2 || ""}
            </div>
            
            {/* Horizontal Line */}
            <hr style={{ border: "none", borderTop: "1px solid #000", margin: "2px 0 3px 0" }} />
            
            {/* Address */}
            <div style={{ fontSize: 10, color: "#000" }}>
              {data.firm.Address1 || ""}
            </div>
          </div>

          {/* Center Section - TAX INVOICE Title */}
          <div style={{ 
            position: "absolute", 
            left: "50%", 
            top: "50%", 
            transform: "translate(-50%, -50%)",
            textAlign: "center"
          }}>
            <div style={{ 
              fontWeight: 700, 
              fontSize: 18, 
              color: "#000",
              textTransform: "uppercase"
            }}>
              TAX INVOICE
            </div>
          </div>

          {/* Right Section - Contact and QR Code */}
          <div style={{ 
            position: "absolute", 
            right: 0, 
            top: 0, 
            width: "35%", 
            textAlign: "right"
          }}>
            {/* Copy Type (e.g., ORIGINAL) */}
            <div style={{ fontWeight: 700, fontSize: 13, color: "#000", marginBottom: 5 }}>
              {copyType}
            </div>
            {/* Mobile Numbers */}
            <div style={{ fontSize: 10, marginBottom: 3, color: "#000" }}>
              Mobile: {data.firm.MobileNo || ""}
            </div>
            
            {/* QR Code - positioned just below mobile numbers */}
            <div style={{ 
              width: "100px", 
              height: "100px", 
              border: "1px solid #ddd",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 10,
              color: "#666",
              wordBreak: "break-all",
              padding: "3px",
              marginLeft: "auto",
              marginRight: 0
            }}>
              {data.EInvoiceData[0]?.SignedQr ? (
                <img 
                  src={`https://api.qrserver.com/v1/create-qr-code/?data=${encodeURIComponent(data.EInvoiceData[0].SignedQr)}&size=120x120`}
                  alt="E-Invoice QR"
                  style={{ 
                    width: "100%", 
                    height: "100%", 
                    objectFit: "contain" 
                  }}
                />
              ) : (
                <div>QR Code</div>
              )}
            </div>
          </div>
        </div>

        {/* Invoice Details Section - Height 2.1cm with precise positioning */}
        <div
          style={{
            height: "2.1cm",
            minHeight: "2.1cm",
            position: "relative",
            color: "#000",
          }}
        >
          {/* Left Column - Company Details */}
          <div
            style={{
              position: "absolute",
              left: 0,
              top: 0,
              width: "30%",
              fontSize: 11,
            }}
          >
            <div style={{ fontWeight: 600, color: "#000", marginBottom: 2 }}>
              M/s : {invoiceData?.PartyName || ""}
            </div>
            <div style={{ color: "#000", fontSize: 10, marginBottom: 1 }}>
              {invoiceData?.Address1 || ""}
            </div>
            <div style={{ color: "#000", fontSize: 10, marginBottom: 1 }}>
              {invoiceData?.Address2 || ""}
            </div>
            <div style={{ fontWeight: 600, fontSize: 10, color: "#000" }}>
              GST No-{" "}
              {invoiceData?.GSTPO
                ? invoiceData.GSTPO.replace("GST No- ", "")
                : ""}
            </div>
          </div>

          {/* Invoice Details - Positioned 8cm from left */}
          <div
            style={{ position: "absolute", left: "8cm", top: 0, fontSize: 11 }}
          >
            <div style={{ display: "flex", marginBottom: 2 }}>
              <span style={{ fontWeight: 600, color: "#000" }}>
                Invoice No. :
              </span>
              <span style={{ marginLeft: 5, color: "#000" }}>
                {invoiceData?.VoucherNo || ""}
              </span>
            </div>
            <div style={{ display: "flex", marginBottom: 2 }}>
              <span style={{ fontWeight: 600, color: "#000" }}>
                Invoice Date :
              </span>
              <span style={{ marginLeft: 5, color: "#000" }}>
                {invoiceData?.Voucher_Date || ""}
              </span>
            </div>
            <div style={{ display: "flex", marginBottom: 2 }}>
              <span style={{ fontWeight: 600, color: "#000" }}>
                Challan No. :
              </span>
              <span style={{ marginLeft: 5, color: "#000" }}>
                {invoiceData?.ChallanNo || ""}
              </span>
            </div>
            <div style={{ display: "flex", marginBottom: 2 }}>
              <span style={{ fontWeight: 600, color: "#000" }}>
                Challan Date :
              </span>
              <span style={{ marginLeft: 5, color: "#000" }}>
                {invoiceData?.Challan_Date || ""}
              </span>
            </div>
            <div style={{ display: "flex", marginBottom: 2 }}>
              <span style={{ fontWeight: 600, color: "#000" }}>
                Vehicle No. :
              </span>
              <span style={{ marginLeft: 5, color: "#000" }}>
                {invoiceData?.VehicleNo || ""}
              </span>
            </div>
          </div>

          {/* GR and PO Details - Positioned 15.6cm from left */}
          <div
            style={{
              position: "absolute",
              left: "15.6cm",
              top: 0,
              fontSize: 11,
            }}
          >
            <div style={{ display: "flex", marginBottom: 2 }}>
              <span style={{ fontWeight: 600, color: "#000" }}>
                P.O. No. :
              </span>
              <span style={{ marginLeft: 5, color: "#000" }}>
                {invoiceData?.PONo || ""}
              </span>
            </div>
            <div style={{ display: "flex", marginBottom: 2 }}>
              <span style={{ fontWeight: 600, color: "#000" }}>P.O. Date :</span>
              <span style={{ marginLeft: 5, color: "#000" }}>
                {invoiceData?.PODate || ""}
              </span>
            </div>
            <div style={{ display: "flex", marginBottom: 2 }}>
              <span style={{ fontWeight: 600, color: "#000" }}>Transport :</span>
              <span style={{ marginLeft: 5, color: "#000" }}>
                {invoiceData?.Transport || ""}
              </span>
            </div>
            <div style={{ display: "flex", marginBottom: 2 }}>
              <span style={{ fontWeight: 600, color: "#000" }}>
                Supply Place :
              </span>
              <span style={{ marginLeft: 5, color: "#000" }}>
                {invoiceData?.PlaceOfSupply || ""}
              </span>
            </div>
          </div>
        </div>

        {/* Item Table with reduced spacing */}
        <div
          className="item-table-container"
          style={{ height: "11.5cm", maxHeight: "11.5cm" }}
        >
          <table
            className="section-spacing"
            style={{
              width: "100%",
              borderCollapse: "collapse",
              margin: "5px 0 0 0",
              fontSize: 12,
              border: "2px solid #222",
            }}
          >
            <ItemTableHeader />
            <tbody>
              {Array.isArray(data.items) &&
                data.items.map((item, index) => {
                  // Skip if item is null or undefined
                  if (!item) return null;

                  // Check if row is empty
                  const isEmptyRow =
                    !item.Counter &&
                    !item.Description1 &&
                    !item.CodeNo &&
                    !item.Description2 &&
                    !item.Description3 &&
                    !item.Qty &&
                    !item.Rate &&
                    !item.Amount;

                  // Instead of skipping empty rows, we'll render them with no border and reduced height
                  return (
                    <tr
                      className="item-row"
                      key={index}
                      style={{
                        borderBottom: isEmptyRow ? "none" : "1px solid #ddd",
                        height: isEmptyRow ? "20px" : "auto",
                        lineHeight: "1.2",
                      }}
                    >
                      <td
                        style={{
                          ...tableCellCenter,
                          fontSize: 13,
                          padding: isEmptyRow ? "0px" : "2px",
                        }}
                      >
                        {item.Counter || ""}
                      </td>
                      <td
                        style={{
                          ...tableCellLeft,
                          fontSize: 13,
                          padding: isEmptyRow ? "0px" : "2px",
                        }}
                      >
                        {item.CodeNo || ""}
                      </td>
                      <td
                        style={{
                          ...tableCellLeft,
                          fontSize: 13,
                          padding: isEmptyRow ? "0px" : "2px",
                        }}
                      >
                        {item.Description1 || ""}
                      </td>
                      <td
                        style={{
                          ...tableCellLeft,
                          fontSize: 13,
                          padding: isEmptyRow ? "0px" : "2px",
                        }}
                      >
                        {item.Description2 || ""}
                      </td>
                      <td
                        style={{
                          ...tableCellLeft,
                          fontSize: 13,
                          padding: isEmptyRow ? "0px" : "2px",
                        }}
                      >
                        {item.Description3 || ""}
                      </td>
                      <td
                        style={{
                          ...tableCellLeft,
                          fontSize: 13,
                          padding: isEmptyRow ? "0px" : "2px",
                        }}
                      >
                        {item.HSNCode || ""}
                      </td>
                      <td
                        style={{
                          ...tableCellRight,
                          fontSize: 13,
                          padding: isEmptyRow ? "0px" : "2px",
                        }}
                      >
                        {item.Qty || ""}
                      </td>
                      <td
                        style={{
                          ...tableCellRight,
                          fontSize: 13,
                          padding: isEmptyRow ? "0px" : "2px",
                        }}
                      >
                        {item.Rate ? formatNumber(item.Rate) : ""}
                      </td>
                      <td
                        style={{
                          ...tableCellRight,
                          fontSize: 13,
                          padding: isEmptyRow ? "0px" : "2px",
                        }}
                      >
                        {item.Amount ? formatNumber(item.Amount) : ""}
                      </td>
                    </tr>
                  );
                })}
            </tbody>
          </table>
        </div>

        {/* Post-Items Section with reduced spacing */}
        <div
          className="after-items-section section-spacing"
          style={{ marginTop: 8, borderTop: "2px solid #222", paddingTop: 5 }}
        >
          {/* E-Invoice and QR Code Layout */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              marginBottom: 8,
              height: "4.2cm",
              maxHeight: "4.2cm",
            }}
          >
            {/* Left Section - E-Invoice Details */}
            <div style={{ width: "35%", fontSize: 11, color: "#000" }}>
              <div
                style={{
                  display: "flex",
                  marginBottom: 3,
                  alignItems: "flex-start",
                }}
              >
                <span
                  style={{
                    fontWeight: 600,
                    color: "#000",
                    whiteSpace: "nowrap",
                  }}
                >
                  IRN :
                </span>
                <span
                  style={{
                    marginLeft: 5,
                    color: "#000",
                    wordBreak: "break-all",
                    flex: 1,
                  }}
                >
                  {data.EInvoiceData[0]?.IRN || ""}
                </span>
              </div>
              <div style={{ display: "flex", marginBottom: 3 }}>
                <span style={{ fontWeight: 600, color: "#000" }}>AckNo :</span>
                <span style={{ marginLeft: 5, color: "#000" }}>
                  {data.EInvoiceData[0]?.AckNo || ""}
                </span>
              </div>
              <div style={{ display: "flex", marginBottom: 3 }}>
                <span style={{ fontWeight: 600, color: "#000" }}>AckDt :</span>
                <span style={{ marginLeft: 5, color: "#000" }}>
                  {data.EInvoiceData[0]?.AckDt || ""}
                </span>
              </div>

              {/* Our Bank Details */}
              <div
                style={{
                  marginTop: 8,
                  paddingTop: 5,
                  borderTop: "1px solid #ddd",
                }}
              >
                <div
                  style={{ fontWeight: 700, marginBottom: 3, color: "#000" }}
                >
                  Our Bank :
                </div>
                <div style={{ marginBottom: 2 }}>
                  <span style={{ fontWeight: 600, color: "#000" }}>Bank :</span>
                  <span style={{ marginLeft: 5, color: "#000" }}>
                    {data.firm.BankName || ""}
                  </span>
                </div>
                <div style={{ marginBottom: 2 }}>
                  <span style={{ fontWeight: 600, color: "#000" }}>A/C :</span>
                  <span style={{ marginLeft: 5, color: "#000" }}>
                    {data.firm.AccountNo || ""}
                  </span>
                </div>
                <div style={{ marginBottom: 2 }}>
                  <span style={{ fontWeight: 600, color: "#000" }}>IFSC :</span>
                  <span style={{ marginLeft: 5, color: "#000" }}>
                    {data.firm.IFSC || ""}
                  </span>
                </div>
              </div>
            </div>

            {/* Center Section - Tax Summary Table */}
            <div style={{ width: "40%", textAlign: "center" }}>
              <table style={{ 
                width: "100%", 
                borderCollapse: "collapse", 
                fontSize: 11, 
                border: "1px solid #222",
                margin: "0 auto"
              }}>
                <tbody>
                  {data.taxSummary && data.taxSummary.map((taxItem, index) => (
                    <tr key={index}>
                      <td style={{ border: "1px solid #ddd", padding: "3px 5px", fontWeight: 600, background: "#f5f5f5", color: "#000", fontSize: 11, textAlign: "left" }}>
                        {taxItem.Name || ""}
                      </td>
                      <td style={{ border: "1px solid #ddd", padding: "3px 5px", textAlign: "right", color: "#000", fontSize: 11 }}>
                        {taxItem.Amount ? formatNumber(taxItem.Amount) : "0.00"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Right Section - Financial Summary */}
            <div style={{ width: "20%", fontSize: 11, color: "#000" }}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  marginBottom: 3,
                }}
              >
                <span style={{ fontWeight: 600, color: "#000" }}>Total</span>
                <span style={{ color: "#000" }}>
                  {data.taxSummary?.[0]?.Amount
                    ? formatNumber(data.taxSummary[0].Amount)
                    : "0.00"}
                </span>
              </div>

              {/* GST Breakdown Table */}
              <table
                style={{
                  width: "100%",
                  borderCollapse: "collapse",
                  fontSize: 10,
                  marginBottom: 3,
                }}
              >
                <tbody>
                  <tr>
                    <td
                      style={{
                        border: "1px solid #ddd",
                        padding: "2px 3px",
                        color: "#000",
                      }}
                    >
                      {data.taxBreakup?.[0]?.TaxName || ""}
                    </td>
                    <td
                      style={{
                        border: "1px solid #ddd",
                        padding: "2px 3px",
                        textAlign: "right",
                        color: "#000",
                      }}
                    >
                      {data.taxBreakup?.[0]?.AddAmt
                        ? formatNumber(data.taxBreakup[0].AddAmt)
                        : "0.00"}
                    </td>
                  </tr>
                  <tr>
                    <td
                      style={{
                        border: "1px solid #ddd",
                        padding: "2px 3px",
                        color: "#000",
                      }}
                    >
                      {data.taxBreakup?.[1]?.TaxName || ""}
                    </td>
                    <td
                      style={{
                        border: "1px solid #ddd",
                        padding: "2px 3px",
                        textAlign: "right",
                        color: "#000",
                      }}
                    >
                      {data.taxBreakup?.[1]?.AddAmt
                        ? formatNumber(data.taxBreakup[1].AddAmt)
                        : "0.00"}
                    </td>
                  </tr>
                </tbody>
              </table>

              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  marginBottom: 3,
                }}
              >
                <span style={{ fontWeight: 600, color: "#000" }}>
                  Round Off
                </span>
                <span style={{ color: "#000" }}>
                  {data.totals?.roundOff !== undefined &&
                  data.totals.roundOff !== null
                    ? formatNumber(data.totals.roundOff)
                    : "0.00"}
                </span>
              </div>
            </div>
          </div>

          {/* Round Off Row with reduced spacing */}

          {/* Amount in words with reduced spacing */}

          <div style={{ height: "0.8cm", maxHeight: "0.8cm" }}>
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                fontSize: 12,
                borderTop: "2px solid #222",
                marginTop: 3,
              }}
            >
              <tbody>
                <tr>
                  <td
                    width="75%"
                    style={{
                      textAlign: "left",
                      padding: "4px",
                      color: "#000",
                      fontSize: 11,
                    }}
                  ></td>
                  <td
                    style={{
                      textAlign: "right",
                      padding: "4px",
                      fontWeight: 700,
                      fontSize: 13,
                      color: "#000",
                      whiteSpace: "nowrap",
                    }}
                  >
                    Grand Total:
                  </td>
                  <td
                    style={{
                      textAlign: "right",
                      padding: "4px",
                      fontWeight: 700,
                      fontSize: 13,
                      color: "#000",
                      width: "25%",
                    }}
                  >
                    {data.totals?.grandTotal !== undefined &&
                    data.totals.grandTotal !== null
                      ? formatNumber(data.totals.grandTotal)
                      : "0.00"}
                  </td>
                </tr>
              </tbody>
            </table>

            <hr
              style={{
                border: "none",
                borderTop: "1px solid #222",
                margin: "4px 0 2px 0",
              }}
            />
          </div>

          {/* Combined Amount Words and Terms Section with Signature */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              marginBottom: 5,
              marginTop: 3,
              height: "2.0cm",
              maxHeight: "2.0cm",
            }}
          >
            {/* Left Section - Amount Words and Terms & Conditions */}
            <div
              style={{
                width: "60%",
                fontSize: 11,
                color: "#000",
                lineHeight: 1.2,
              }}
            >
              {/* Amount in words section */}
              <div style={{ marginBottom: 8 }}>
                <div style={{ marginBottom: 2 }}>
                  <span style={{ fontWeight: 600 }}>Amount in words: </span>
                  <span style={{ fontStyle: "italic" }}>
                    {invoiceData?.NetAmountInWords || ""}
                  </span>
                </div>
                <div>
                  <span style={{ fontWeight: 600 }}>Tax Amount in words: </span>
                  <span style={{ fontStyle: "italic" }}>
                    {invoiceData?.TaxationAmountInWords || ""}
                  </span>
                </div>
              </div>

              {/* Terms and Conditions section */}
              <div>
                <div
                  style={{
                    fontWeight: 700,
                    marginBottom: 2,
                    color: "#000",
                    fontSize: 9,
                  }}
                >
                  Terms & Conditions :
                </div>
                <ol style={{ paddingLeft: 15, margin: 0, fontSize: 9 }}>
                  <li style={{ marginBottom: 0, lineHeight: 1.2 }}>
                    Our Firm Registered in MSME.
                  </li>
                  <li style={{ marginBottom: 0, lineHeight: 1.2 }}>
                    Subject to Jodhpur Jurisdiction only.
                  </li>
                  <li style={{ marginBottom: 0, lineHeight: 1.2 }}>
                    No Claim will be entertained after dispatch of Goods.
                  </li>
                  <li style={{ lineHeight: 1.2 }}>E & O.E..</li>
                </ol>
              </div>
            </div>

            {/* Right Section - Signature */}
            <div
              className="signature-section"
              style={{ width: "30%", fontSize: 12, color: "#000" }}
            >
              <div
                style={{ textAlign: "right", fontWeight: 600, lineHeight: 1.2 }}
              >
                <div className="signature-name-row">
                  For <b>{data.firm.FirmName}</b>
                </div>
                {/* This is a spacer specifically for signatures */}
                <div
                  className="signature-space"
                  style={{ height: 15, border: "0px dashed transparent" }}
                ></div>
                <div className="signature-title-row">Authorized Signatory</div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom spacing of 1.5 cm */}
        <div style={{ height: "1.5cm", minHeight: "1.5cm" }}></div>
      </div>

      {/* Print Button - will not show when printing */}
      <div
        className="no-print"
        style={{ textAlign: "center", marginTop: 20, marginBottom: 40 }}
      >
        <div style={{ marginBottom: 15 }}>
          <select
            value={copyType}
            onChange={e => setCopyType(e.target.value)}
            style={{
              padding: "8px 15px",
              borderRadius: 4,
              border: "1px solid #ccc",
              marginRight: 10,
              fontSize: 14,
            }}
          >
            <option value="ORIGINAL">ORIGINAL</option>
            <option value="DUPLICATE">DUPLICATE</option>
            <option value="TRIPLICATE">TRIPLICATE</option>
          </select>
          <span style={{ fontSize: 14 }}>Select copy type</span>
        </div>

        <button
          onClick={handlePrint}
          style={{
            backgroundColor: "#2a41cc",
            color: "white",
            border: "none",
            borderRadius: 5,
            padding: "12px 30px",
            fontSize: 16,
            fontWeight: 600,
            cursor: "pointer",
            boxShadow: "0 2px 8px rgba(0,0,0,0.3)",
          }}
        >
          Print Invoice
        </button>
      </div>
    </div>
  );
};

// Define the prop types for validation
InvoiceForUsha.propTypes = {
  invoiceArray: PropTypes.arrayOf(
    PropTypes.shape({
      VoucherNo: PropTypes.string,
      Voucher_Date: PropTypes.string,
      ChallanNo: PropTypes.string,
      PartyName: PropTypes.string,
      GSTPO: PropTypes.string,
      Address2: PropTypes.string,
      GrossAmount: PropTypes.number,
      TaxationAmount: PropTypes.number,
      NetAmount: PropTypes.number,
      PONo: PropTypes.string,
      VehicleNo: PropTypes.string,
      GrossAmountInWords: PropTypes.string,
      TaxationAmountInWords: PropTypes.string,
      NetAmountInWords: PropTypes.string,
      ConsigneeAddress1: PropTypes.string,
      ConsigneeAddress2: PropTypes.string,
      ConsigneeAddress3: PropTypes.string,
      RoundedValue: PropTypes.number,
      PlaceOfSupply: PropTypes.string,
    })
  ),
  firmArray: PropTypes.arrayOf(
    PropTypes.shape({
      GST: PropTypes.string,
      FirmName: PropTypes.string,
      Address1: PropTypes.string,
      Address2: PropTypes.string,
      MobileNo: PropTypes.string,
      BankName: PropTypes.string,
      AccountNo: PropTypes.string,
      IFSC: PropTypes.string,
      MsmeNo: PropTypes.string,
    })
  ),
  items: PropTypes.arrayOf(
    PropTypes.shape({
      Counter: PropTypes.number,
      SNo: PropTypes.number,
      Id: PropTypes.string,
      CodeNo: PropTypes.string,
      Description1: PropTypes.string,
      Description2: PropTypes.string,
      Description3: PropTypes.string,
      HSNCode: PropTypes.string,
      Qty: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      Rate: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      ItemUnit: PropTypes.string,
      Amount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      ItemType: PropTypes.string,
      Tax: PropTypes.string,
    })
  ),
  taxBreakup: PropTypes.arrayOf(
    PropTypes.shape({
      TaxName: PropTypes.string,
      AddAmt: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    })
  ),
  taxSummary: PropTypes.arrayOf(
    PropTypes.shape({
      Name: PropTypes.string,
      Amount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    })
  ),
  totals: PropTypes.shape({
    taxable: PropTypes.number,
    tax: PropTypes.number,
    cgst: PropTypes.number,
    sgst: PropTypes.number,
    roundOff: PropTypes.number,
    grandTotal: PropTypes.number,
  }),
};

// Define default props
InvoiceForUsha.defaultProps = {
  invoiceArray: [],
  firmArray: [],
  items: null, // null will make the component use the default items in the data object
  taxBreakup: null,
  taxSummary: null,
  totals: null,
};

export default InvoiceForUsha;
